type FullscreenElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
  webkitRequestFullScreen?: () => Promise<void> | void;
};

type FullscreenDocument = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
};

export function isIos(): boolean {
  const ua = navigator.userAgent;
  const ipadOs13Plus =
    navigator.platform === 'MacIntel' && typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints > 1;
  return /iPhone|iPad|iPod/.test(ua) || ipadOs13Plus;
}

/** True when running as an installed home-screen app, which on iOS is the only real full-screen mode. */
export function inStandalone(): boolean {
  if ((navigator as unknown as { standalone?: boolean }).standalone === true) return true;
  return (
    typeof window.matchMedia === 'function' &&
    (window.matchMedia('(display-mode: standalone)').matches ||
      window.matchMedia('(display-mode: fullscreen)').matches)
  );
}

export function fullscreenSupported(): boolean {
  const el = document.documentElement as FullscreenElement;
  return (
    typeof el.requestFullscreen === 'function' ||
    typeof el.webkitRequestFullscreen === 'function' ||
    typeof el.webkitRequestFullScreen === 'function'
  );
}

export function isFullscreen(): boolean {
  const doc = document as FullscreenDocument;
  return Boolean(document.fullscreenElement ?? doc.webkitFullscreenElement);
}

/**
 * iPhone Safari has never shipped the Fullscreen API, and iPad only got the webkit-prefixed element
 * form in iOS 12 — so this is best-effort. When it is unavailable the frame relies on the home-screen
 * standalone mode instead, which is why the onboarding flow pushes "Add to Home Screen" so hard.
 */
export async function toggleFullscreen(): Promise<void> {
  const doc = document as FullscreenDocument;
  const el = document.documentElement as FullscreenElement;

  if (isFullscreen()) {
    if (typeof document.exitFullscreen === 'function') await document.exitFullscreen();
    else if (typeof doc.webkitExitFullscreen === 'function') await doc.webkitExitFullscreen();
    return;
  }

  if (typeof el.requestFullscreen === 'function') await el.requestFullscreen();
  else if (typeof el.webkitRequestFullscreen === 'function') await el.webkitRequestFullscreen();
  else if (typeof el.webkitRequestFullScreen === 'function') await el.webkitRequestFullScreen();
}

interface WakeLockSentinel {
  released: boolean;
  release(): Promise<void>;
  addEventListener(type: 'release', listener: () => void): void;
}

/**
 * Keeps the screen awake where the API exists (Chrome 84+ on desktop and Android). Safari has no
 * Wake Lock, so on iOS the only option is Settings → Display & Brightness → Auto-Lock → Never; the
 * onboarding text says so explicitly rather than leaving the frame to go dark after a minute.
 */
export function keepScreenAwake(): () => void {
  const nav = navigator as unknown as { wakeLock?: { request(type: 'screen'): Promise<WakeLockSentinel> } };
  if (!nav.wakeLock) return () => {};

  let sentinel: WakeLockSentinel | null = null;
  let released = false;

  const acquire = async () => {
    if (released || document.hidden) return;
    try {
      sentinel = await nav.wakeLock!.request('screen');
      // The browser drops the lock whenever the page is hidden; re-acquire on the way back.
      sentinel.addEventListener('release', () => {
        sentinel = null;
      });
    } catch {
      // Denied (not visible, battery saver, insecure context) — nothing to do but carry on.
    }
  };

  const onVisibility = () => {
    if (!document.hidden && sentinel === null) void acquire();
  };

  void acquire();
  document.addEventListener('visibilitychange', onVisibility);

  return () => {
    released = true;
    document.removeEventListener('visibilitychange', onVisibility);
    void sentinel?.release().catch(() => {});
  };
}
